import React, { useCallback, useEffect, useRef, useState } from 'react';

// Edit-mode drag handles for the commercial documents' tables (`table.cmp-table`).
//
//   - Drag a vertical line between two columns to move it left/right. The two columns either
//     side of the line trade width, so the table always stays exactly 100% wide.
//   - Drag the line under a row to make that row taller/shorter. Text wraps to the new width
//     by itself.
//
// Design notes:
//   - The handles live in an overlay that is a SIBLING of the printable content, never inside it,
//     so they can't end up in the saved HTML or in print. Only inline `style` changes on the
//     tables/cells themselves are written, and those are exactly what the save captures.
//   - All positions are taken from getBoundingClientRect relative to the overlay's own origin,
//     which keeps the maths right under the page's CSS `zoom` font control.
//   - Changes are remembered per element so "Reset table layout" can undo everything done since
//     the page was opened.

const MIN_COL_PCT = 3;
const MIN_ROW_PX = 14;

const HANDLE_CSS = `
  .tbl-rs-col, .tbl-rs-row { position: absolute; pointer-events: auto; touch-action: none; }
  .tbl-rs-col { width: 9px; margin-left: -4px; cursor: col-resize; z-index: 6; }
  .tbl-rs-row { height: 9px; margin-top: -4px; cursor: row-resize; z-index: 5; }
  .tbl-rs-col::after { content: ''; position: absolute; left: 4px; top: 0; bottom: 0; width: 1px; background: rgba(59,130,246,0.35); }
  .tbl-rs-row::after { content: ''; position: absolute; top: 4px; left: 0; right: 0; height: 1px; background: rgba(59,130,246,0.35); }
  .tbl-rs-col:hover::after, .tbl-rs-col.tbl-rs-active::after { left: 3px; width: 3px; background: #3b82f6; }
  .tbl-rs-row:hover::after, .tbl-rs-row.tbl-rs-active::after { top: 3px; height: 3px; background: #3b82f6; }
`;

const TableResizer = ({ containerRef, enabled, resetSignal = 0 }) => {
  const rootRef = useRef(null);
  const [handles, setHandles] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const originals = useRef(new Map()); // element -> style.cssText before our first change
  const frozen = useRef(new WeakSet()); // tables whose column widths are already pinned
  const frame = useRef(0);

  const measure = useCallback(() => {
    const root = rootRef.current;
    const container = containerRef.current;
    if (!root || !container) return;
    const o = root.getBoundingClientRect();
    const out = [];
    container.querySelectorAll('table.cmp-table').forEach((table, t) => {
      const tr = table.getBoundingClientRect();
      if (!tr.width) return;
      const ths = table.querySelectorAll('thead tr:first-child > th');
      for (let i = 0; i < ths.length - 1; i += 1) {
        const r = ths[i].getBoundingClientRect();
        out.push({ key: `c-${t}-${i}`, kind: 'col', t, i, x: r.right - o.left, y: tr.top - o.top, len: tr.height });
      }
      Array.from(table.rows).forEach((row, r) => {
        const rr = row.getBoundingClientRect();
        out.push({ key: `r-${t}-${r}`, kind: 'row', t, r, x: tr.left - o.left, y: rr.bottom - o.top, len: tr.width });
      });
    });
    setHandles(out);
  }, [containerRef]);

  const schedule = useCallback(() => {
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      measure();
    });
  }, [measure]);

  // Keep handles glued to the borders as the document changes size (typing, font control,
  // row spacing, window resize).
  useEffect(() => {
    if (!enabled) {
      setHandles([]);
      return undefined;
    }
    measure();
    const container = containerRef.current;
    let observer;
    if (container && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(schedule);
      observer.observe(container);
    }
    window.addEventListener('resize', schedule);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', schedule);
      if (frame.current) {
        cancelAnimationFrame(frame.current);
        frame.current = 0;
      }
    };
  }, [enabled, containerRef, measure, schedule]);

  // "Reset table layout": put back every style we touched.
  useEffect(() => {
    if (!resetSignal) return;
    originals.current.forEach((css, el) => {
      if (css) el.style.cssText = css;
      else el.removeAttribute('style');
    });
    originals.current.clear();
    frozen.current = new WeakSet();
    schedule();
  }, [resetSignal, schedule]);

  const remember = (el) => {
    if (!originals.current.has(el)) originals.current.set(el, el.getAttribute('style') || '');
  };

  // Pin every column of a table to its current width as a % of the table, and switch to fixed
  // layout, so a drag moves exactly the two columns either side of the line. A no-op visually.
  const freezeColumns = (table, ths) => {
    if (frozen.current.has(table)) return;
    const tableW = table.getBoundingClientRect().width;
    remember(table);
    const widths = ths.map((th) => th.getBoundingClientRect().width);
    ths.forEach((th, idx) => {
      remember(th);
      th.style.width = `${((widths[idx] / tableW) * 100).toFixed(3)}%`;
      th.style.boxSizing = 'border-box';
    });
    table.style.tableLayout = 'fixed';
    frozen.current.add(table);
  };

  // Visual px per layout px inside the (possibly CSS-zoomed) document.
  const layoutScale = (table) => {
    const host = table.parentElement || table;
    const probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;visibility:hidden;width:100px;height:0;';
    host.appendChild(probe);
    const scale = probe.getBoundingClientRect().width / 100;
    host.removeChild(probe);
    return scale > 0 ? scale : 1;
  };

  const startDrag = (e, h) => {
    const container = containerRef.current;
    if (!container) return;
    const table = container.querySelectorAll('table.cmp-table')[h.t];
    if (!table) return;
    e.preventDefault();
    e.stopPropagation();
    setActiveKey(h.key);

    let onMove;
    if (h.kind === 'col') {
      const ths = Array.from(table.querySelectorAll('thead tr:first-child > th'));
      freezeColumns(table, ths);
      const tableW = table.getBoundingClientRect().width;
      const left = ths[h.i];
      const right = ths[h.i + 1];
      const l0 = parseFloat(left.style.width);
      const r0 = parseFloat(right.style.width);
      const x0 = e.clientX;
      onMove = (ev) => {
        let d = ((ev.clientX - x0) / tableW) * 100;
        d = Math.max(MIN_COL_PCT - l0, Math.min(r0 - MIN_COL_PCT, d));
        left.style.width = `${(l0 + d).toFixed(3)}%`;
        right.style.width = `${(r0 - d).toFixed(3)}%`;
        schedule();
      };
    } else {
      const row = table.rows[h.r];
      if (!row) return;
      remember(row);
      const scale = layoutScale(table);
      const h0 = row.getBoundingClientRect().height / scale;
      const y0 = e.clientY;
      onMove = (ev) => {
        const next = Math.max(MIN_ROW_PX, h0 + (ev.clientY - y0) / scale);
        row.style.height = `${next.toFixed(1)}px`;
        schedule();
      };
    }

    // No text selection while dragging across the document.
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    const onUp = () => {
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      setActiveKey(null);
      schedule();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  return (
    <div
      ref={rootRef}
      className="no-print"
      style={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0, overflow: 'visible' }}
    >
      {enabled && (
        <>
          <style>{HANDLE_CSS}</style>
          {handles.map((h) => (
            <div
              key={h.key}
              className={`${h.kind === 'col' ? 'tbl-rs-col' : 'tbl-rs-row'}${activeKey === h.key ? ' tbl-rs-active' : ''}`}
              title={h.kind === 'col' ? 'Drag to resize column' : 'Drag to resize row'}
              onPointerDown={(e) => startDrag(e, h)}
              style={
                h.kind === 'col'
                  ? { left: h.x, top: h.y, height: h.len }
                  : { left: h.x, top: h.y, width: h.len }
              }
            />
          ))}
        </>
      )}
    </div>
  );
};

export default TableResizer;
